import { validateNativePortableText } from './native-text-contract';
import { resourceValidationIds } from './resource-validation-ids';
import { parseMaipoPublicScene, sceneSupportDiagnostics, type MaipoPublicSceneV01 } from '@maipo/format';
import { createId, walkLayers, type EditorDocument, type Layer } from '../core/model';
import { documentPublicResources } from './adapter';

const METADATA = 'maipo:public-document';
const FONT_REF = 'maipo:public-font-ref';
export type LayerResources = Pick<MaipoPublicSceneV01, 'fonts' | 'assets' | 'extensionDeclarations'>;

export function layerNeedsResources(layer: Layer): boolean {
  return layer.kind === 'group' ? layer.children.some(layerNeedsResources) : layer.kind === 'image'
    || (layer.kind === 'text' && (layer.text.schemaVersion === 2 || layer.extensions[FONT_REF] !== undefined));
}

function metadata(document: EditorDocument): Record<string, unknown> {
  const value = document.extensions[METADATA];
  if (value === undefined) return { format: 'maipo-scene', version: '0.1', revision: 0 };
  if (!value || typeof value !== 'object' || Array.isArray(value) || (value as Record<string, unknown>).format !== 'maipo-scene' || (value as Record<string, unknown>).version !== '0.1') throw new Error('Unsupported public document resource metadata.');
  return structuredClone(value as Record<string, unknown>);
}

/** Clipboard owns its resource snapshot, even after the source tab closes. */
export function captureLayerResources(document: EditorDocument): LayerResources {
  const stored = metadata(document);
  const resources = documentPublicResources(document);
  const validationId = resourceValidationIds(resources.fonts, resources.assets);
  const scene = parseMaipoPublicScene({
    format: 'maipo-scene', version: '0.1', documentId: validationId(), revision: 0, name: 'Native resources',
    canvas: { width: 1, height: 1, colorSpace: 'srgb', background: { type: 'transparent' } },
    ...resources, children: [],
    ...(stored.extensionDeclarations === undefined ? {} : { extensionDeclarations: stored.extensionDeclarations }),
  });
  const unsupported = sceneSupportDiagnostics(scene);
  if (unsupported.length) throw new Error(unsupported.map(item => item.message).join(' '));
  return structuredClone({ fonts: scene.fonts, assets: scene.assets, ...(scene.extensionDeclarations ? { extensionDeclarations: scene.extensionDeclarations } : {}) });
}

function stable(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stable).join(',')}]`;
  if (value && typeof value === 'object') return `{${Object.entries(value).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0).map(([key, item]) => `${JSON.stringify(key)}:${stable(item)}`).join(',')}}`;
  return JSON.stringify(value) ?? 'null';
}
function identity(value: { id: string }): string {
  return stable(Object.fromEntries(Object.entries(value).filter(([key]) => key !== 'id')));
}

/** Plan on detached layers/tables; never mutate the destination or source. */
export function mergeLayerResources(source: LayerResources, target: EditorDocument, detachedLayer: Layer, options: { rebindPortableText?: boolean } = {}): Record<string, unknown> {
  const existing = captureLayerResources(target);
  const result = metadata(target);
  const fonts = structuredClone(existing.fonts);
  const assets = structuredClone(existing.assets);
  const declarations = structuredClone(existing.extensionDeclarations ?? []);
  for (const declaration of source.extensionDeclarations ?? []) {
    const previous = declarations.find(item => item.key === declaration.key);
    if (previous && stable(previous) !== stable(declaration)) throw new Error(`Public extension ${declaration.key} has conflicting declarations in these documents.`);
    if (!previous) declarations.push(structuredClone(declaration));
  }
  // Reserve every native/public ID, including vector geometry and mask IDs.
  const claimed = new Set<string>([target.id, ...fonts.map(item => item.id), ...assets.map(item => item.id)]);
  const visitIds = (value: unknown): void => {
    if (!value || typeof value !== 'object' || ArrayBuffer.isView(value)) return;
    if (Array.isArray(value)) { value.forEach(visitIds); return; }
    if (typeof (value as { id?: unknown }).id === 'string') claimed.add((value as { id: string }).id);
    for (const [key, child] of Object.entries(value)) if (key !== 'raster' && key !== 'tiles') visitIds(child);
  };
  visitIds(target.root); visitIds(detachedLayer);
  const claim = (preferred: string, prefix: string): string => {
    let id = preferred;
    while (claimed.has(id)) id = createId(prefix);
    claimed.add(id); return id;
  };
  const references = new Map<string, string>();
  const importedAssets = new Map<string, string>();
  const importAsset = (id: string): string => {
    const previous = importedAssets.get(id);
    if (previous) return previous;
    const sourceAsset = source.assets.find(asset => asset.id === id);
    if (!sourceAsset) throw new Error(`Copied asset ${id} is missing its resource table.`);
    const same = assets.find(asset => identity(asset) === identity(sourceAsset));
    const mapped = same?.id ?? claim(sourceAsset.id, 'asset');
    if (!same) assets.push({ ...structuredClone(sourceAsset), id: mapped });
    importedAssets.set(id, mapped); return mapped;
  };
  const imageRemaps: { layer: Extract<Layer, { kind: 'image' }>; ref: string }[] = [];
  const layers = detachedLayer.kind === 'group' ? walkLayers(detachedLayer) : [detachedLayer];
  const portableRemaps: { layer: Extract<Layer, { kind: 'text' }>; refs: string[] }[] = [];
  const portableRefs = new Set(layers.flatMap(layer => layer.kind === 'text' && layer.text.schemaVersion === 2 ? layer.text.portable?.fonts ?? [] : []));
  const importFont = (ref: string): string => {
    const previous = references.get(ref); if (previous) return previous;
    const original = source.fonts.find(font => font.id === ref);
    if (!original) throw new Error(`Copied font ${ref} is missing its resource table.`);
    const font = structuredClone(original);
    if (font.source.kind === 'embedded') font.source.asset = importAsset(font.source.asset);
    // Ordered candidate lists require distinct IDs, even for equivalent faces.
    const reserved = portableRefs.has(ref) ? new Set([...references].filter(([source]) => portableRefs.has(source)).map(([, mapped]) => mapped)) : new Set<string>();
    const same = fonts.find(item => !reserved.has(item.id) && identity(item) === identity(font));
    const mapped = same?.id ?? claim(font.id, 'font');
    if (!same) fonts.push({ ...font, id: mapped });
    references.set(ref, mapped); return mapped;
  };
  // Validate all references before changing even the detached layer.
  const remaps: { layer: Layer; ref: string }[] = [];
  for (const layer of layers) {
    if (layer.kind === 'text' && layer.text.schemaVersion === 2) {
      if (!options.rebindPortableText) throw new Error('Transferring portable text requires layout rebinding.');
      validateNativePortableText(layer.text);
      portableRemaps.push({ layer, refs: layer.text.portable!.fonts.map(importFont) });
    }
    if (layer.kind === 'image') {
      const original = source.assets.find(asset => asset.id === layer.image.asset);
      if (!original?.mediaType.startsWith('image/')) throw new Error('Copied image has no image asset.');
      imageRemaps.push({ layer, ref: importAsset(layer.image.asset) });
      continue;
    }
    if (layer.kind !== 'text' || layer.extensions[FONT_REF] === undefined) continue;
    const ref = layer.extensions[FONT_REF];
    if (typeof ref !== 'string') throw new Error('Invalid copied public font reference.');
    remaps.push({ layer, ref: importFont(ref) });
  }
  for (const { layer, refs } of portableRemaps) layer.text.portable!.fonts = refs;
  for (const { layer, ref } of imageRemaps) layer.image.asset = ref;
  for (const { layer, ref } of remaps) layer.extensions[FONT_REF] = ref;
  return { ...result, fonts, assets, extensionDeclarations: declarations };
}
